import { useEffect, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import api from "@/lib/api";
import { useDebounce } from "@/hooks/useDebounce";
import type { PaginatedQueryResult } from "@/types";

interface AsyncSelectProps<T> {
  url: string;
  getOptionLabel: (item: T) => string;
  getOptionValue: (item: T) => string;
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  filterOption?: (item: T) => boolean;
}

function AsyncSelect<T>({
  url,
  getOptionLabel,
  getOptionValue,
  value,
  onChange,
  placeholder,
  disabled = false,
  filterOption,
}: AsyncSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<T[]>([]);
  const debouncedSearch = useDebounce(search, 500);

  const selectedOption =
    options.find((o) => getOptionValue(o) === value) ?? null;
  const displayLabel = selectedOption
    ? getOptionLabel(selectedOption)
    : (value ?? "");

  useEffect(() => {
    api
      .get<PaginatedQueryResult<T>>(url, {
        params: { search: debouncedSearch || undefined },
      })
      .then((res) => setOptions(res.data.results))
      .catch(() => setOptions([]));
  }, [debouncedSearch, url]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
          )}
        >
          <span className="truncate">
            {value ? displayLabel : (placeholder ?? "")}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>Aucun résultat</CommandEmpty>
            <CommandGroup>
              {(filterOption ? options.filter(filterOption) : options).map(
                (item) => (
                  <CommandItem
                    key={getOptionValue(item)}
                    value={getOptionValue(item)}
                    onSelect={(val) => {
                      onChange(val === value ? null : val);
                      setOpen(false);
                    }}
                  >
                    {getOptionLabel(item)}
                  </CommandItem>
                ),
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default AsyncSelect;
